import { ExclamationTriangleIcon, UserPlusIcon } from '@heroicons/react/24/outline'
import type {
  UhcAllocationPreview,
  UhcAuditNote,
  UhcEmployeeLocationsValidation,
} from '@/services/uhcAllocations'

export const INPUT_MISSING_CLASS =
  'border-red-500 ring-2 ring-red-500 focus:border-red-500 focus:ring-red-500'

export function isFieldEmpty(value: string | null | undefined): boolean {
  return !String(value ?? '').trim()
}

export function locationRowHasMissingFields(row: {
  uhc_subscriber_name: string
  homebase_employee_id?: string | null
  homebase_first_name?: string | null
  homebase_last_name?: string | null
  home_store: string
}): boolean {
  return (
    isFieldEmpty(row.uhc_subscriber_name) ||
    isFieldEmpty(row.homebase_employee_id) ||
    isFieldEmpty(row.homebase_first_name) ||
    isFieldEmpty(row.homebase_last_name) ||
    isFieldEmpty(row.home_store)
  )
}

export function inputClassName(missing: boolean, base: string): string {
  return missing ? `${base} ${INPUT_MISSING_CLASS}` : base
}

const NOT_IN_LOCATIONS_PREFIX = 'NOT IN EMPLOYEE_LOCATIONS'

export function parseEmployeeLocationSection(section: string): {
  kind: 'not_in_locations' | 'list' | 'text'
  title: string
  names: string[]
  body: string
} {
  const colonIdx = section.indexOf(': ')
  if (colonIdx === -1) {
    return { kind: 'text', title: section, names: [], body: '' }
  }

  const title = section.slice(0, colonIdx)
  const body = section.slice(colonIdx + 2).trim()

  if (title === NOT_IN_LOCATIONS_PREFIX || title === 'MISSING HOME STORE') {
    return {
      kind: title === NOT_IN_LOCATIONS_PREFIX ? 'not_in_locations' : 'list',
      title,
      names: body
        .split(',')
        .map((name) => name.trim())
        .filter(Boolean),
      body,
    }
  }

  return { kind: 'text', title, names: [], body }
}

function parseAdjustmentDetail(line: string): {
  subscriber: string
  code: string
  summary: string
} {
  const trimmed = line.trim().replace(/^•\s*/, '')
  const match = trimmed.match(/^(.+?)\s+\[([A-Z]+)\]:\s*(.+)$/)
  if (!match) {
    return { subscriber: trimmed, code: '', summary: '' }
  }
  return {
    subscriber: (match[1] ?? '').trim(),
    code: match[2] ?? '',
    summary: (match[3] ?? '').trim(),
  }
}

const ADJ_CODE_STYLES: Record<string, string> = {
  ADD: 'bg-emerald-50 text-emerald-800 ring-emerald-100',
  TRM: 'bg-red-50 text-red-800 ring-red-100',
  CHG: 'bg-amber-50 text-amber-900 ring-amber-100',
}

function adjustmentCodeClass(code: string): string {
  return ADJ_CODE_STYLES[code] ?? 'bg-gray-100 text-gray-700 ring-gray-200'
}

const CONFLICT_PREFIXES = [
  'CONFLICTING HOME STORE (same employee, different rows)',
  'CONFLICTING HOME STORE (same UHC subscriber name)',
  'INVALID HOME STORE',
]

export function UhcAuditEmployeeIssues({
  validation,
  onAddMissing,
  addingMissing,
}: {
  validation: UhcEmployeeLocationsValidation
  onAddMissing?: (names: string[]) => void
  addingMissing?: boolean
}) {
  const conflictSections = validation.sections.filter((section) =>
    CONFLICT_PREFIXES.some((prefix) => section.startsWith(prefix))
  )

  const hasListIssues =
    validation.not_in_locations.length > 0 || validation.missing_home_store.length > 0

  if (!hasListIssues && conflictSections.length === 0) return null

  return (
    <div className="space-y-3">
      {validation.not_in_locations.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-red-200 bg-red-50">
          <div className="flex items-start gap-3 px-4 py-3">
            <ExclamationTriangleIcon
              className="mt-0.5 h-5 w-5 shrink-0 text-red-600"
              aria-hidden
            />
            <div className="min-w-0 flex-1 space-y-3">
              <div>
                <h4 className="text-sm font-semibold text-red-900">Not in Employee locations</h4>
                <p className="mt-1 text-xs text-red-700">
                  {validation.not_in_locations.length} subscriber
                  {validation.not_in_locations.length !== 1 ? 's' : ''} on this invoice need a
                  mapping
                </p>
              </div>

              <div className="flex flex-wrap gap-1.5">
                {validation.not_in_locations.map((name) => (
                  <span
                    key={name}
                    className="inline-flex rounded-md bg-white px-2 py-1 text-xs font-medium text-red-900 ring-1 ring-red-200"
                  >
                    {name}
                  </span>
                ))}
              </div>

              {onAddMissing ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-red-900 shadow-sm ring-1 ring-red-300 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={() => onAddMissing(validation.not_in_locations)}
                  disabled={addingMissing}
                >
                  <UserPlusIcon className="h-4 w-4" />
                  {addingMissing ? 'Adding…' : 'Add all to Employee locations'}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {validation.missing_home_store.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-red-200 bg-red-50">
          <div className="flex items-start gap-3 px-4 py-3">
            <ExclamationTriangleIcon
              className="mt-0.5 h-5 w-5 shrink-0 text-red-600"
              aria-hidden
            />
            <div className="min-w-0 flex-1 space-y-3">
              <div>
                <h4 className="text-sm font-semibold text-red-900">Missing home store</h4>
                <p className="mt-1 text-xs text-red-700">
                  {validation.missing_home_store.length} subscriber
                  {validation.missing_home_store.length !== 1 ? 's' : ''} need a home store
                  assignment
                </p>
              </div>

              <div className="flex flex-wrap gap-1.5">
                {validation.missing_home_store.map((name) => (
                  <span
                    key={name}
                    className="inline-flex rounded-md bg-white px-2 py-1 text-xs font-medium text-red-900 ring-1 ring-red-200"
                  >
                    {name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {conflictSections.map((section, i) => {
        const parsed = parseEmployeeLocationSection(section)
        return (
          <div
            key={`emp-conflict-${i}`}
            className="overflow-hidden rounded-lg border border-red-200 bg-red-50"
          >
            <div className="flex items-start gap-3 px-4 py-3">
              <ExclamationTriangleIcon
                className="mt-0.5 h-5 w-5 shrink-0 text-red-600"
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                <h4 className="text-sm font-semibold text-red-900">{parsed.title}</h4>
                <p className="mt-1 text-sm leading-relaxed text-red-800">{parsed.body}</p>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export function UhcAuditNotesList({ notes }: { notes: UhcAuditNote[] }) {
  if (notes.length === 0) return null

  return (
    <div className="space-y-3">
      {notes.map((note, i) => {
        const details = note.details ?? []
        const isAdjustments = note.category === 'adjustments' && details.length > 0

        return (
          <div
            key={`${note.category}-${i}`}
            className="overflow-hidden rounded-lg border border-amber-200 bg-amber-50"
          >
            <div className="flex items-start gap-3 px-4 py-3">
              <ExclamationTriangleIcon
                className="mt-0.5 h-5 w-5 shrink-0 text-amber-600"
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                <h4 className="text-sm font-semibold text-amber-950">{note.message}</h4>

                {details.length > 0 ? (
                  isAdjustments ? (
                    <div className="mt-3 rounded-md border border-amber-200/80 bg-white">
                      <table className="min-w-full text-left text-xs">
                        <thead className="bg-amber-50/95 text-[11px] font-semibold uppercase tracking-wide text-amber-900">
                          <tr>
                            <th className="px-3 py-2">Subscriber</th>
                            <th className="px-3 py-2">Code</th>
                            <th className="px-3 py-2">Details</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-amber-100">
                          {details.map((line, lineIdx) => {
                            const parsed = parseAdjustmentDetail(line)
                            return (
                              <tr key={`adj-${lineIdx}`} className="text-amber-950">
                                <td className="px-3 py-2 font-medium whitespace-nowrap">
                                  {parsed.subscriber}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  {parsed.code ? (
                                    <span
                                      className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${adjustmentCodeClass(parsed.code)}`}
                                    >
                                      {parsed.code}
                                    </span>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                                <td className="px-3 py-2 text-amber-900">{parsed.summary || '—'}</td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <ul className="mt-2 space-y-1 text-sm text-amber-950">
                      {details.map((line, lineIdx) => (
                        <li key={`detail-${lineIdx}`} className="leading-relaxed">
                          {line.trim().replace(/^•\s*/, '')}
                        </li>
                      ))}
                    </ul>
                  )
                ) : null}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function formatUsd(value: number | string): string {
  const num = typeof value === 'string' ? Number(value) : value
  if (!Number.isFinite(num)) return String(value)
  return num.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  })
}

/** Store allocation cards for the audit report — post-adjustment split used for the JE. */
export function AllocationPreviewCards({
  after,
}: {
  before: UhcAllocationPreview | null
  after: UhcAllocationPreview | null
}) {
  if (!after) return <p className="text-sm text-gray-500">No allocation preview available.</p>
  const cards: Array<{ label: string; amount: number | string }> = [
    { label: 'Highland Park', amount: after.highland_park },
    { label: 'River Oaks', amount: after.river_oaks },
    { label: 'G&A', amount: after.g_and_a },
  ]
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {cards.map((c) => (
          <div key={c.label} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
            <div className="text-xs font-medium text-gray-500">{c.label}</div>
            <div className="mt-1 text-lg font-semibold text-gray-900">{formatUsd(c.amount)}</div>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-500">
        Total {formatUsd(after.total)} · JE reallocates {formatUsd(after.reallocation_total)} from G&A
        to Highland Park / River Oaks
      </p>
    </div>
  )
}
